import { useState } from 'react';
import { toast } from 'sonner';
import { Lightning, PencilSimple, X as XIcon, ArrowsClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { itApi } from '../itApi';

// Omada 書き戻し ― 名前変更 / 固定IP / 解放 を一つのダイアログで扱う。
// 実際の反映はルーターの sync agent が最大10分間隔で拾って PATCH する。
export default function OmadaWriteDialog({ open, onClose, entry, onDone }) {
  const [name, setName] = useState('');
  const [fixedIp, setFixedIp] = useState('');
  const [busy, setBusy] = useState(false);
  const canWrite = entry?.source === 'omada' && entry?.mac_address;

  const reset = () => { setName(''); setFixedIp(''); };

  const send = async (body, msg) => {
    setBusy(true);
    try {
      await itApi.omadaWrite(entry.id, body);
      toast.success(msg, { description: 'Omadaへの反映まで最大10分かかります' });
      onDone?.();
      onClose();
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '送信に失敗しました');
    } finally { setBusy(false); }
  };

  const doRename = () => {
    const v = name.trim();
    if (!v) return toast.error('新しい名前を入力してください');
    send({ name: v }, `「${v}」にリネームを予約しました`);
  };
  const doFixedIp = () => {
    const v = fixedIp.trim() || entry?.ip_address;
    if (!v) return toast.error('固定IPを入力してください');
    if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(v)) return toast.error('IPアドレスの形式が不正です');
    send({ fixed_ip: v }, `固定IP ${v} を予約しました`);
  };
  const doRelease = () => {
    if (!window.confirm(`「${entry?.device_name || entry?.ip_address}」の固定IPを解放しますか？（次回DHCP更新から動的IPになります）`)) return;
    send({ clear_fixed_ip: true }, '固定IP解放を予約しました');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="omada-write-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <Lightning size={17} weight="fill" className="text-amber-500" /> Omadaへ書き戻し
          </DialogTitle>
        </DialogHeader>

        {entry && (
          <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-2.5 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100">Omada</Badge>
              <span className="font-semibold text-zinc-800">{entry.device_name || entry.ip_address}</span>
              {entry.fixed_ip && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">固定IP: {entry.fixed_ip}</Badge>}
            </div>
            <p className="mt-1 font-mono text-[10px] text-zinc-500">MAC: {entry.mac_address || '—'} ／ 現在IP: {entry.ip_address}</p>
          </div>
        )}

        {!canWrite ? (
          <p className="text-xs text-zinc-500 py-2">このエントリはOmada管理ではないか、MACが未取得のため書き戻せません。</p>
        ) : (
          <div className="space-y-4">
            {/* リネーム */}
            <div>
              <label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
                <PencilSimple size={11} weight="bold" /> Omada上の名前を変更
              </label>
              <div className="flex gap-2 mt-1">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={entry?.device_name || '例: 開発PC-05'}
                  disabled={busy} className="h-9" data-testid="omada-write-name-input" />
                <Button onClick={doRename} disabled={busy || !name.trim()} className="h-9 bg-teal-700 hover:bg-teal-800 shrink-0" data-testid="omada-write-rename-btn">送信</Button>
              </div>
            </div>

            {/* 固定IP */}
            <div>
              <label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
                <Lightning size={11} weight="bold" /> 固定IPに設定 <span className="font-normal tracking-normal text-zinc-400">(DHCP予約)</span>
              </label>
              <div className="flex gap-2 mt-1">
                <Input value={fixedIp} onChange={(e) => setFixedIp(e.target.value)} placeholder={entry?.ip_address || '192.168.x.x'}
                  disabled={busy} className="h-9 font-mono" data-testid="omada-write-fixedip-input" />
                <Button onClick={doFixedIp} disabled={busy} className="h-9 bg-teal-700 hover:bg-teal-800 shrink-0" data-testid="omada-write-fixedip-btn">送信</Button>
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">未入力の場合は現在のIP ({entry?.ip_address}) を固定します</p>
            </div>

            {/* 解放 */}
            {entry?.fixed_ip && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-2.5">
                <label className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1">
                  <XIcon size={11} weight="bold" /> 固定IPを解放
                </label>
                <p className="text-[10px] text-amber-700 mt-0.5 mb-1.5">現在 {entry.fixed_ip} に予約されています。解放すると次回DHCP更新から動的IPになります。</p>
                <Button onClick={doRelease} disabled={busy} variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="omada-write-release-btn">
                  <XIcon size={12} className="mr-1" /> 固定IP解放を予約
                </Button>
              </div>
            )}

            <p className="text-[10px] text-zinc-400 border-t border-zinc-200 pt-2 flex items-center gap-1">
              <ArrowsClockwise size={10} className="text-zinc-400" /> ルーターの同期エージェント（10分毎）が実際にOmadaへ反映します
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); reset(); }} disabled={busy} data-testid="omada-write-cancel-btn">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
